import { Component, OnInit, DestroyRef, inject, signal } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { NavigationItem } from '@cvx/nextpage';
import { CalAngularService, ICvxClaimsPrincipal } from '@cvx/cal-angular';
import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserDetails } from './core/models/app-user.models';
import { NAVIGATION_ITEMS } from './core/constants/app-navigation.constants';
import { AuthTokenService } from './core/services/app-auth-token.service';
import { LoadingService } from './core/services/app-loading.service';
import { Footer } from './layouts/footer/footer';
import { Header } from './layouts/header/header';
import { Spinner } from './layouts/spinner/spinner';
import { map } from 'rxjs/internal/operators/map';
import { ApiService } from './core/services/app-api.service';
import { Toast, ToastService } from './core/services/app-toast.service';
import { concat, of, switchMap, timer } from 'rxjs';

const TOAST_DURATION_MS = 5000;

@Component({
  standalone: true,
  imports: [Header, Footer, RouterOutlet, Spinner],
  selector: 'app-root',
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit {
  navigationItems: NavigationItem[] = NAVIGATION_ITEMS;
  welcomeMessage = signal('');
  isInitializing = signal(true);
  toastMessage = signal<Toast | null>(null);

  private readonly calService = inject(CalAngularService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly tokenService = inject(AuthTokenService);
  private readonly loadingService = inject(LoadingService);
  private readonly apiService = inject(ApiService);
  private readonly toastService = inject(ToastService);

  currentUserProfile!: ICvxClaimsPrincipal;

  constructor() {
    this.toastService.toastState$
      .pipe(
        switchMap((message) =>
          concat(
            of(message),
            timer(TOAST_DURATION_MS).pipe(map((): null => null)),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((message) => this.toastMessage.set(message));
  }

  dismissToast(): void {
    this.toastMessage.set(null);
  }

  async ngOnInit(): Promise<void> {
    this.loadingService.show();
    await this.logIn();

    // const user = this.getUserDetails();
    // user.subscribe((userDetails) => {
    //   console.log('userDetails => ', userDetails);
    // });

    // this.calService.getAADToken().subscribe((token) => {
    //   console.log('token => ', token);
    //   if (token) {
    //     sessionStorage.setItem('MSALToken', JSON.stringify(token));
    //     this.currentUserProfile = this.calService.cvxClaimsPrincipal;
    //     console.log('currentUserProfile => ', this.currentUserProfile);
    //   }
    // });

    // this.apiService.getQuotaDetails().subscribe((quotaDetails) => {
    //   console.log('quotaDetails => ', quotaDetails);
    // });
  }

  getUserDetails() {
    return this.calService
      .getUserPropertiesFromMsGraph(['givenName', 'cai', 'jobTitle', 'photo', 'surname'])
      .pipe(
        map((user: any) => {
          if (user) return user;
        }),
      );
  }

  /** Logs in the user by fetching the impersonation token and initializing the user session if signed in. */
  async logIn() {
    await this.getUserImpersonationToken();
    this.calService
      .isUserSignedIn()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isSignedIn) => {
        if (isSignedIn) {
          void this.initializeUserSession().finally(() => this.completeInitialization());
        }
      });
  }

  /** Hides the startup spinner and reveals the application once CAL and the user session are ready. */
  private completeInitialization(): void {
    this.isInitializing.set(false);
    this.loadingService.hide();
  }

  /** Fetches the user impersonation token from the authentication service */
  async getUserImpersonationToken(): Promise<void> {
    try {
      await this.tokenService.getUserImpersonationToken();
    } catch (error) {
      console.error('Error while fetching user impersonation token:', error);
    }
  }

  /** Initializes the user session after successful sign-in */
  private async initializeUserSession(): Promise<void> {
    const data = await lastValueFrom(this.calService.userInitiatedSignIn());
    const name = data?.name ?? '';
    if (!name) {
      return;
    }
    const cleanedName = name.replace(/\s*\[.*\]/, '').replace(/\s*,\s*/, ' ');
    const [lastName, firstName] = cleanedName.split(' ');
    const user: UserDetails = {
      firstName,
      lastName,
      emailId: data.email,
      userId: data.objectId,
      fullName: `${firstName} ${lastName}`,
      roles: data.roles,
      isUser: data.isUser,
      cai: data.logOnName,
    };
    this.welcomeMessage.set(user.fullName);
    sessionStorage.setItem('currentUser', JSON.stringify(user));
  }
}
